import { z } from 'zod'

const listSortPreferenceSchema = z.object({
  order: z.array(z.string()),
  updatedAt: z.string().datetime(),
})

export const sortPreferencesSchema = z.object({
  version: z.literal(1),
  lists: z.record(z.string(), listSortPreferenceSchema),
})

export type SortPreferences = z.infer<typeof sortPreferencesSchema>
export const EMPTY_SORT_PREFERENCES: SortPreferences = { version: 1, lists: {} }

export function updateSortPreference(
  preferences: SortPreferences,
  list: string,
  order: ReadonlyArray<string>,
): SortPreferences {
  const ids = new Set(order)
  // Unfetched items may still belong to the list.
  const unseen = preferences.lists[list]?.order.filter((id) => !ids.has(id)) ?? []
  return {
    ...preferences,
    lists: {
      ...preferences.lists,
      [list]: { order: [...ids, ...unseen], updatedAt: new Date().toISOString() },
    },
  }
}

/** Drops ids from a list's saved order, so any that come back sort as new. */
export function forgetSortItems(
  preferences: SortPreferences,
  list: string,
  ids: ReadonlyArray<string>,
): SortPreferences {
  const saved = preferences.lists[list]
  const order = saved?.order.filter((id) => !ids.includes(id))
  if (!saved || !order || order.length === saved.order.length) {
    return preferences
  }
  return {
    ...preferences,
    lists: { ...preferences.lists, [list]: { order, updatedAt: new Date().toISOString() } },
  }
}

export function applySortPreference<T>(
  items: ReadonlyArray<T>,
  order: ReadonlyArray<string>,
  getKey: (item: T) => string,
): Array<T> {
  const positions = new Map(order.map((id, index) => [id, index]))
  return [...items].sort((a, b) => (positions.get(getKey(a)) ?? -1) - (positions.get(getKey(b)) ?? -1))
}

export function moveSortItem(
  order: ReadonlyArray<string>,
  source: string,
  target: string,
  placement: 'before' | 'after',
): Array<string> {
  if (source === target || !order.includes(source) || !order.includes(target)) {
    return [...order]
  }
  const next = order.filter((id) => id !== source)
  next.splice(next.indexOf(target) + (placement === 'after' ? 1 : 0), 0, source)
  return next
}
